import { useEffect, useRef, type CSSProperties } from 'react';
import { iconDataUri, type TabDef } from '@ce/core';
import { kicker, t, divider } from '../styles';

/**
 * The arc field that bleeds off the top-right of every header — the Nocturne
 * signature. Two hairline rings plus a soft radial bloom, never a filled shape.
 */
export function HeaderArcs() {
  const ring = (size: number, top: number, right: number, opacity: number, delay: number): CSSProperties => ({
    position: 'absolute',
    top,
    right,
    width: size,
    height: size,
    borderRadius: '50%',
    border: `1px solid rgba(var(--color-accent-rgb), ${opacity})`,
    pointerEvents: 'none',
    // Offset so the two rings never breathe in unison, which would read as a
    // throb rather than as atmosphere.
    animation: `arcBreathe 9s ease-in-out ${delay}s infinite`,
    willChange: 'transform, opacity',
  });
  return (
    <>
      <div className="ambient-anim" style={ring(380, -190, -150, 0.16, 0)} />
      <div className="ambient-anim" style={ring(260, -140, -90, 0.1, 1.8)} />
      <div
        style={{
          position: 'absolute', top: -120, right: -60, width: 240, height: 240,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(var(--color-accent-rgb), 0.14), transparent 70%)',
          pointerEvents: 'none',
        }}
      />
    </>
  );
}

/** A single decorative ring, for cards that carry the motif at smaller scale. */
export function CardArc({ size, top, right, opacity = 0.18, pulse, delay = 0 }: {
  size: number; top: number; right: number; opacity?: number;
  /** Slow outward ping. Off by default — most arcs sit behind text. */
  pulse?: boolean; delay?: number;
}) {
  return (
    <div
      aria-hidden
      className={pulse ? 'ambient' : undefined}
      style={{
        position: 'absolute', right, top, width: size, height: size,
        borderRadius: '50%',
        // Template-literal alpha, which is why the earlier sweep to theme
        // tokens missed this one — it only matched numeric alphas.
        border: `1px solid rgba(var(--color-accent-rgb), ${opacity})`,
        pointerEvents: 'none',
        ...(pulse
          ? {
              animation: `arcPulse 7s cubic-bezier(.2,.6,.3,1) ${delay}s infinite`,
              willChange: 'transform, opacity',
            }
          : null),
      }}
    />
  );
}

export function Header({
  kicker: k,
  title,
  cartCount,
  unread,
  showGrid,
  show,
  onMenu,
  onGrid,
  onSearch,
  onMessages,
  onCart,
}: {
  kicker: string;
  title: string;
  cartCount: number;
  unread: number;
  /** Which utility buttons the config allows. */
  show: { search: boolean; inbox: boolean; cart: boolean };
  /** Present only for the drawer style. */
  onMenu?: () => void;
  /** The list/grid toggle only appears where there is a schedule to reshape. */
  showGrid: boolean;
  onGrid: () => void;
  onSearch: () => void;
  onMessages: () => void;
  onCart: () => void;
}) {
  return (
    <header style={{ flex: 'none', padding: '56px 20px 12px', position: 'relative', overflow: 'hidden' }}>
      <HeaderArcs />
      <div
        style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          gap: 12, position: 'relative',
        }}
      >
        {onMenu && (
          <button
            type="button"
            onClick={onMenu}
            aria-label="Open menu"
            style={{
              flex: 'none', width: 38, height: 38, borderRadius: 10, cursor: 'pointer',
              border: `1px solid ${divider}`, background: 'transparent',
              display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4, padding: '0 10px',
            }}
          >
            <span style={{ height: 1.5, borderRadius: 1, background: t(0.75) }} />
            <span style={{ height: 1.5, width: '70%', borderRadius: 1, background: 'var(--color-accent)' }} />
            <span style={{ height: 1.5, borderRadius: 1, background: t(0.75) }} />
          </button>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={kicker(9.5, 'var(--color-accent-300)', 0.2)}>{k}</div>
          <h3 style={{ fontSize: 25, letterSpacing: '-0.02em', margin: '7px 0 0', fontWeight: 500 }}>
            {title}
          </h3>
        </div>
        <div style={{ display: 'flex', gap: 8, flex: 'none' }}>
          {showGrid && (
          <button
            type="button"
            onClick={onGrid}
            title="Grid view"
            aria-label="Grid view"
            style={{
              width: 38, height: 38, flex: 'none', borderRadius: 10,
              border: `1px solid ${divider}`, background: 'transparent', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
              justifyContent: 'center', gap: 3, padding: '0 9px',
            }}
          >
            <div style={{ height: 2, width: '100%', borderRadius: 2, background: 'var(--color-accent)' }} />
            <div style={{ height: 2, width: '62%', borderRadius: 2, background: t(0.55) }} />
            <div style={{ height: 2, width: '84%', borderRadius: 2, background: t(0.35) }} />
          </button>
          )}
          {show.search && (
          <button
            type="button"
            onClick={onSearch}
            title="Search"
            aria-label="Search"
            style={{
              width: 38, height: 38, flex: 'none', borderRadius: 10,
              border: `1px solid ${divider}`, background: 'transparent', cursor: 'pointer',
              display: 'grid', placeItems: 'center',
            }}
          >
            {/* Ring plus handle — the magnifier drawn with the arc motif's geometry. */}
            <div style={{ position: 'relative', width: 17, height: 17 }}>
              <div
                style={{
                  position: 'absolute', top: 0, left: 0, width: 12, height: 12,
                  borderRadius: '50%', border: `1.5px solid ${t(0.75)}`,
                }}
              />
              <div
                style={{
                  position: 'absolute', bottom: 1, right: 1, width: 7, height: 1.5,
                  borderRadius: 1, background: t(0.75), transform: 'rotate(45deg)',
                  transformOrigin: 'right center',
                }}
              />
            </div>
          </button>
          )}
          {show.inbox && (
          <button
            type="button"
            onClick={onMessages}
            title="Inbox"
            aria-label={unread ? `Inbox, ${unread} unread` : 'Inbox'}
            style={{
              position: 'relative', width: 38, height: 38, flex: 'none', borderRadius: 10,
              border: `1px solid ${divider}`, background: 'transparent', cursor: 'pointer',
              display: 'grid', placeItems: 'center',
            }}
          >
            {/* A bell drawn from primitives — a bowl, a rim and a clapper — so it
                inherits the theme like the rest of the chrome. */}
            <div style={{ position: 'relative', width: 16, height: 17 }}>
              <div
                style={{
                  position: 'absolute', top: 1, left: 2, width: 12, height: 11,
                  borderRadius: '6px 6px 2px 2px', border: `1.5px solid ${t(0.75)}`,
                  borderBottom: 'none',
                }}
              />
              <div
                style={{
                  position: 'absolute', top: 11.5, left: 0, width: 16, height: 1.5,
                  borderRadius: 1, background: t(0.75),
                }}
              />
              <div
                style={{
                  position: 'absolute', top: 14, left: 6, width: 4, height: 3,
                  borderRadius: '0 0 3px 3px', background: t(0.75),
                }}
              />
            </div>
            {unread > 0 && (
              <div
                style={{
                  position: 'absolute', top: -4, right: -4, minWidth: 16, height: 16,
                  padding: '0 4px', borderRadius: 8, background: 'var(--color-accent)',
                  color: 'var(--color-bg)', font: '600 9.5px/16px var(--font-body)',
                  textAlign: 'center',
                }}
              >
                {unread > 9 ? '9+' : unread}
              </div>
            )}
          </button>
          )}

          {show.cart && (
          <button
            type="button"
            onClick={onCart}
            aria-label={`Cart, ${cartCount} items`}
            style={{
              position: 'relative', width: 38, height: 38, flex: 'none', borderRadius: 10,
              border: `1px solid ${divider}`, background: 'transparent', cursor: 'pointer',
              display: 'grid', placeItems: 'center',
            }}
          >
            <div
              style={{
                width: 15, height: 12, border: `1.5px solid ${t(0.75)}`,
                borderRadius: '2px 2px 4px 4px',
              }}
            />
            {cartCount > 0 && (
              <div
                style={{
                  position: 'absolute', top: -5, right: -5, minWidth: 17, height: 17,
                  padding: '0 4px', borderRadius: 9, background: 'var(--color-accent)',
                  color: 'var(--color-bg)', font: '600 10px/17px var(--font-body)', textAlign: 'center',
                }}
              >
                {cartCount}
              </div>
            )}
          </button>
          )}
        </div>
      </div>
    </header>
  );
}

/**
 * A tab or menu icon, tinted from the theme.
 * Drawn as a CSS mask over a data URI from `@ce/core`, so an icon chosen in the
 * admin exists here by construction — there is no file to forget to ship, and
 * no base-path to get wrong.
 */
function TabIcon({ icon, color, size = 20 }: { icon: string; color: string; size?: number }) {
  const uri = `url("${iconDataUri(icon)}")`;
  return (
    <span
      aria-hidden
      style={{
        width: size + 4, height: size + 4, flex: 'none', backgroundColor: color,
        WebkitMask: `${uri} center/${size}px ${size}px no-repeat`,
        mask: `${uri} center/${size}px ${size}px no-repeat`,
        transition: 'background-color .18s ease',
      }}
    />
  );
}

interface NavProps {
  tabs: TabDef[];
  active: string | null;
  showLabels: boolean;
  onSelect: (tab: TabDef) => void;
}

/** Bottom tabs, and the floating dock — the same items, a different container. */
export function TabBar({ tabs, active, showLabels, onSelect, floating }: NavProps & { floating?: boolean }) {
  return (
    <nav
      aria-label="Main"
      style={
        floating
          ? {
              position: 'absolute', left: 14, right: 14, zIndex: 20,
              bottom: 'max(14px, env(safe-area-inset-bottom, 0px))',
              display: 'flex', gap: 2, padding: '8px 8px', borderRadius: 'calc(var(--radius-lg) + 8px)',
              background: 'rgba(var(--color-bg-rgb), 0.82)',
              backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
              border: `1px solid ${divider}`, boxShadow: 'var(--shadow-md)',
              animation: 'settle .5s cubic-bezier(.2,.8,.2,1) both',
            }
          : {
              flex: 'none', display: 'flex', gap: 2, padding: '8px 12px',
              // Ask the platform for the inset instead of reserving 30px for an
              // iOS home indicator that only exists on a real device.
              paddingBottom: 'max(10px, env(safe-area-inset-bottom, 0px))',
              background: 'linear-gradient(to top, var(--color-bg) 60%, rgba(var(--color-bg-rgb), 0.86))',
              borderTop: `1px solid ${divider}`,
            }
      }
    >
      {tabs.map((tab) => {
        const on = active === tab.id;
        const color = on ? 'var(--color-accent-300)' : t(0.45);
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onSelect(tab)}
            aria-current={on ? 'page' : undefined}
            aria-label={showLabels ? undefined : tab.label}
            style={{
              flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 6, padding: '6px 0 4px', border: 0, cursor: 'pointer', color,
              borderRadius: floating ? 'var(--radius-lg)' : 0,
              background: floating && on ? 'rgba(var(--color-accent-rgb), 0.14)' : 'transparent',
              transition: 'background .2s ease',
            }}
          >
            <TabIcon icon={tab.icon} color={color} />
            {showLabels && (
              <span style={{ font: '500 9.5px/1 var(--font-body)', letterSpacing: '0.04em', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {tab.label}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

/** Tabs across the top, under the header. Scrolls when there are many. */
export function TopTabs({ tabs, active, showLabels, onSelect }: NavProps) {
  return (
    <nav
      aria-label="Main"
      className="scroll"
      style={{
        flex: 'none', display: 'flex', gap: 6, overflowX: 'auto', padding: '2px 20px 10px',
        borderBottom: `1px solid ${divider}`,
      }}
    >
      {tabs.map((tab) => {
        const on = active === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onSelect(tab)}
            aria-current={on ? 'page' : undefined}
            style={{
              flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '8px 14px', borderRadius: 999, cursor: 'pointer',
              border: `1px solid ${on ? 'var(--color-accent)' : divider}`,
              background: on ? 'rgba(var(--color-accent-rgb), 0.16)' : 'transparent',
              color: on ? 'var(--color-accent-300)' : t(0.62),
              font: '500 12.5px/1 var(--font-body)',
              transition: 'background .2s ease, border-color .2s ease, color .2s ease',
            }}
          >
            {showLabels ? tab.label : <TabIcon icon={tab.icon} color="currentColor" size={16} />}
          </button>
        );
      })}
    </nav>
  );
}

/**
 * Side drawer. Slides over the content inside the device frame, closes on
 * backdrop, Escape, or a selection. Focus moves in on open so keyboard users
 * are not left behind a panel they cannot reach.
 */
export function Drawer({
  open, tabs, active, title, subtitle, onSelect, onClose,
}: {
  open: boolean; tabs: TabDef[]; active: string | null; title: string; subtitle: string;
  onSelect: (tab: TabDef) => void; onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <div
      aria-hidden={!open}
      style={{
        position: 'absolute', inset: 0, zIndex: 70, pointerEvents: open ? 'auto' : 'none',
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0, background: 'rgba(8,9,16,0.6)',
          opacity: open ? 1 : 0, transition: 'opacity .28s ease',
        }}
      />
      <div
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-label="Menu"
        style={{
          position: 'absolute', top: 0, bottom: 0, left: 0, width: 'min(78%, 310px)', outline: 'none',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          background: 'var(--color-bg)', borderRight: `1px solid ${divider}`,
          boxShadow: 'var(--shadow-lg)',
          transform: open ? 'none' : 'translateX(-104%)',
          transition: 'transform .34s cubic-bezier(.2,.8,.2,1)',
        }}
      >
        <div style={{ position: 'relative', padding: '60px 22px 18px', overflow: 'hidden', borderBottom: `1px solid ${divider}` }}>
          <HeaderArcs />
          <div style={{ position: 'relative' }}>
            <div style={kicker(9.5, 'var(--color-accent-300)', 0.2)}>{subtitle}</div>
            <div style={{ font: '500 22px/1.15 var(--font-body)', letterSpacing: '-0.02em', marginTop: 8 }}>{title}</div>
          </div>
        </div>
        <div className="scroll" style={{ flex: 1, overflow: 'auto', padding: '10px 12px' }}>
          {tabs.map((tab, i) => {
            const on = active === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onSelect(tab)}
                aria-current={on ? 'page' : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left',
                  padding: '13px 12px', marginBottom: 2, border: 0, cursor: 'pointer',
                  borderRadius: 'var(--radius-md)',
                  background: on ? 'rgba(var(--color-accent-rgb), 0.14)' : 'transparent',
                  color: on ? 'var(--color-accent-300)' : 'var(--color-text)',
                  font: '500 15px/1.2 var(--font-body)',
                  opacity: open ? 1 : 0,
                  transform: open ? 'none' : 'translateX(-10px)',
                  transition: `opacity .3s ease ${80 + i * 30}ms, transform .3s cubic-bezier(.2,.8,.2,1) ${80 + i * 30}ms, background .2s ease`,
                }}
              >
                <TabIcon icon={tab.icon} color={on ? 'var(--color-accent-300)' : t(0.6)} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
